/**
 * VideoPlayer Component
 * Custom video player with play/pause button, scrubbing bar, and time display.
 * Replaces native browser controls with design-system-consistent UI.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Play, Pause } from "lucide-react";

interface VideoPlayerProps {
  src: string;
  className?: string;
  style?: React.CSSProperties;
  onError?: () => void;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export const VideoPlayer = React.memo(function VideoPlayer({
  src,
  className = "",
  style,
  onError,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Start/reset the auto-hide timer when playing
  const scheduleHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setShowControls(false), 2500);
  }, []);

  const showControlsNow = useCallback(
    (playing: boolean) => {
      setShowControls(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (playing && !isDragging) scheduleHide();
    },
    [isDragging, scheduleHide],
  );

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  // Seek to ratio [0, 1] of the video duration
  const seekTo = (ratio: number) => {
    const video = videoRef.current;
    if (!video || !isFinite(video.duration)) return;
    const time = Math.max(0, Math.min(1, ratio)) * video.duration;
    video.currentTime = time;
    setCurrentTime(time);
  };

  const getRatioFromEvent = (e: MouseEvent | React.MouseEvent): number => {
    const bar = progressBarRef.current;
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    return (e.clientX - rect.left) / rect.width;
  };

  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const video = videoRef.current;
    const wasPlaying = video ? !video.paused : false;

    // Pause immediately so seeking never accidentally triggers playback
    if (video && !video.paused) video.pause();

    setIsDragging(true);
    seekTo(getRatioFromEvent(e));

    const onMove = (ev: MouseEvent) => seekTo(getRatioFromEvent(ev));
    const onUp = () => {
      setIsDragging(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      // Restore playback state only after the user releases the scrubber
      if (wasPlaying && videoRef.current) {
        videoRef.current.play().catch(() => {});
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const controlsVisible = showControls || !isPlaying || isDragging;

  return (
    <div
      className={`relative overflow-hidden leading-none flex items-center justify-center ${className}`}
      style={style}
      onMouseMove={() => showControlsNow(isPlaying)}
      onMouseEnter={() => showControlsNow(isPlaying)}
      onMouseLeave={() => {
        if (isPlaying && !isDragging) setShowControls(false);
      }}
    >
      {/* Video element – no native controls */}
      <video
        ref={videoRef}
        src={src}
        crossOrigin="use-credentials"
        className="max-w-full max-h-full block cursor-pointer align-bottom object-contain"
        onTimeUpdate={() => {
          if (!isDragging) setCurrentTime(videoRef.current?.currentTime ?? 0);
        }}
        onLoadedMetadata={() => {
          setDuration(videoRef.current?.duration ?? 0);
        }}
        onPlay={() => {
          setIsPlaying(true);
          scheduleHide();
        }}
        onPause={() => {
          setIsPlaying(false);
          setShowControls(true);
          if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        }}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
          setShowControls(true);
          if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        }}
        onError={onError}
        onClick={togglePlay}
      />

      {/* Centered play/pause overlay */}
      <div
        className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-200 ${controlsVisible ? "opacity-100" : "opacity-0"}`}
      >
        <div
          className="bg-black/50 rounded-full p-4 pointer-events-auto cursor-pointer hover:bg-black/70 transition-colors"
          onClick={togglePlay}
        >
          {isPlaying ? (
            <Pause className="h-8 w-8 text-white fill-white" />
          ) : (
            <Play className="h-8 w-8 text-white fill-white" />
          )}
        </div>
      </div>

      {/* Bottom controls bar */}
      <div
        className={`absolute bottom-0 left-0 right-0 px-3 pb-3 pt-8 bg-gradient-to-t from-black/80 to-transparent transition-opacity duration-200 ${controlsVisible ? "opacity-100" : "opacity-0"}`}
      >
        {/* Scrub bar */}
        <div
          ref={progressBarRef}
          className="w-full h-1.5 bg-white/30 rounded-full cursor-pointer mb-2.5 relative group/bar hover:h-2.5 transition-all duration-100"
          onMouseDown={handleProgressMouseDown}
        >
          <div
            className="h-full bg-white rounded-full relative"
            style={{ width: `${progress}%` }}
          >
            {/* Scrub handle */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3.5 h-3.5 bg-white rounded-full shadow opacity-0 group-hover/bar:opacity-100 transition-opacity" />
          </div>
        </div>

        {/* Play button + time */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={togglePlay}
            className="text-white hover:text-white/70 transition-colors flex-shrink-0"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <Pause className="h-4 w-4 fill-white" />
            ) : (
              <Play className="h-4 w-4 fill-white" />
            )}
          </button>
          <span className="text-white/90 text-xs font-mono tabular-nums">
            {formatTime(currentTime)}&nbsp;/&nbsp;{formatTime(duration)}
          </span>
        </div>
      </div>
    </div>
  );
});
